// Offline storage for the Layer7 Workspace PWA.
//
// A tiny, dependency-free IndexedDB layer that backs three things:
//
//   1. OFFLINE READ  — the last-viewed mail list snapshot + individual opened
//      messages/threads are stashed here so the inbox and recently-read mail
//      stay readable with no network (Cache/IDB, not Background Sync).
//   2. OUTBOX        — sends that fail because the device is offline are queued
//      here (see hooks/useOutbox.ts) and flushed on the `online` event, with a
//      Background Sync fallback handled in public/sw.js.
//   3. APP BADGE     — a feature-detected wrapper around navigator.setAppBadge
//      so the unread count can surface on the installed-app icon.
//
// IMPORTANT: the DB name, version, and the OUTBOX store name/shape below are
// mirrored verbatim by the `sync` handler in public/sw.js (which can't import
// this module — a classic service worker has no ES-module graph). If you bump
// DB_VERSION or rename OUTBOX_STORE here, update sw.js in lockstep.

export const DB_NAME = "l7-offline";
export const DB_VERSION = 1;

export const OUTBOX_STORE = "outbox";
const MAIL_LIST_STORE = "mail_list";
const MESSAGE_STORE = "messages";

// How many opened messages/threads to retain before evicting the oldest. Keeps
// the store from growing unbounded across a long session of reading mail.
const MESSAGE_CAP = 200;

// ── Serializable payload shapes ─────────────────────────────────────────────

// Body of POST /api/mail/send (mirrors JmapApi.sendAs' argument). This is what
// the composer hands to the outbox when a send fails offline; the flush re-POSTs
// it verbatim. Attachments ride as already-uploaded Stalwart blob references.
export interface OutboxSendPayload {
  from: string;
  to: { email: string; name?: string }[];
  cc?: { email: string; name?: string }[];
  bcc?: { email: string; name?: string }[];
  subject: string;
  text?: string;
  html?: string;
  in_reply_to?: string;
  references?: string[];
  attachments?: Array<{ blobId: string; name: string; type: string; size?: number }>;
}

export type OutboxStatus = "queued" | "sending" | "error";

export interface OutboxItem {
  id: string;             // crypto.randomUUID()
  payload: OutboxSendPayload;
  createdAt: number;      // ms epoch
  status: OutboxStatus;
  attempts: number;
  lastError?: string;
  // Denormalized for cheap list rendering without re-parsing the payload.
  recipients: string;     // "a@x.com, b@y.com"
  subject: string;
}

// A cached mail-list snapshot. `key` is a stable cache key the caller builds
// from account + mailbox + filter (see mailListKey below). `emails` is whatever
// list-row shape the caller wants back — kept generic so this module stays
// decoupled from the Email type.
export interface CachedMailList {
  key: string;
  savedAt: number;
  label?: string;         // human hint, e.g. "Inbox"
  emails: unknown[];
}

export interface CachedMessage {
  id: string;             // email id or thread id
  savedAt: number;
  data: unknown;          // Email | Email[] — opaque to this module
}

// ── DB bootstrap ────────────────────────────────────────────────────────────

let dbPromise: Promise<IDBDatabase> | null = null;

function idbAvailable(): boolean {
  return typeof indexedDB !== "undefined";
}

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    if (!idbAvailable()) {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(OUTBOX_STORE)) {
        db.createObjectStore(OUTBOX_STORE, { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains(MAIL_LIST_STORE)) {
        db.createObjectStore(MAIL_LIST_STORE, { keyPath: "key" });
      }
      if (!db.objectStoreNames.contains(MESSAGE_STORE)) {
        const s = db.createObjectStore(MESSAGE_STORE, { keyPath: "id" });
        s.createIndex("savedAt", "savedAt", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
  // Reset the memo on hard failure so a later call can retry a fresh open.
  dbPromise.catch(() => { dbPromise = null; });
  return dbPromise;
}

function tx<T>(
  store: string,
  mode: IDBTransactionMode,
  run: (s: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = run(t.objectStore(store));
        t.oncomplete = () => resolve(req.result);
        t.onabort = () => reject(t.error ?? new Error("IndexedDB tx aborted"));
        t.onerror = () => reject(t.error ?? new Error("IndexedDB tx error"));
      }),
  );
}

function getAll<T>(store: string): Promise<T[]> {
  return tx<T[]>(store, "readonly", (s) => s.getAll() as IDBRequest<T[]>);
}

// ── Outbox ──────────────────────────────────────────────────────────────────

export async function outboxAdd(item: OutboxItem): Promise<void> {
  try {
    await tx(OUTBOX_STORE, "readwrite", (s) => s.put(item));
  } catch {
    /* storage unavailable — caller surfaces the raw send error instead */
  }
}

export async function outboxAll(): Promise<OutboxItem[]> {
  try {
    const items = await getAll<OutboxItem>(OUTBOX_STORE);
    return items.sort((a, b) => a.createdAt - b.createdAt);
  } catch {
    return [];
  }
}

export async function outboxRemove(id: string): Promise<void> {
  try {
    await tx(OUTBOX_STORE, "readwrite", (s) => s.delete(id));
  } catch {
    /* best effort */
  }
}

export async function outboxUpdate(
  id: string,
  patch: Partial<Omit<OutboxItem, "id">>,
): Promise<void> {
  try {
    const existing = await tx<OutboxItem | undefined>(
      OUTBOX_STORE,
      "readonly",
      (s) => s.get(id) as IDBRequest<OutboxItem | undefined>,
    );
    if (!existing) return;
    await tx(OUTBOX_STORE, "readwrite", (s) => s.put({ ...existing, ...patch }));
  } catch {
    /* best effort */
  }
}

// Build a fresh outbox row from a send payload.
export function newOutboxItem(payload: OutboxSendPayload): OutboxItem {
  const recipients = [
    ...payload.to.map((t) => t.email),
    ...(payload.cc?.map((t) => t.email) ?? []),
    ...(payload.bcc?.map((t) => t.email) ?? []),
  ].join(", ");
  return {
    id: cryptoRandomId(),
    payload,
    createdAt: Date.now(),
    status: "queued",
    attempts: 0,
    recipients,
    subject: payload.subject,
  };
}

function cryptoRandomId(): string {
  try {
    if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `ob_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

// ── Mail list snapshot (offline read) ────────────────────────────────────────

// Stable cache key for a list view. Kept deliberately simple + collision-free
// across the axes the inbox varies on.
export function mailListKey(
  accountId: string,
  mailboxId: string | null,
  filter?: string,
  label?: string | null,
): string {
  return [accountId, mailboxId ?? "_", filter ?? "all", label ?? "_"].join("|");
}

export async function cacheMailList(
  key: string,
  emails: unknown[],
  label?: string,
): Promise<void> {
  try {
    const rec: CachedMailList = { key, emails, label, savedAt: Date.now() };
    await tx(MAIL_LIST_STORE, "readwrite", (s) => s.put(rec));
  } catch {
    /* best effort */
  }
}

export async function readMailList(key: string): Promise<CachedMailList | null> {
  try {
    const rec = await tx<CachedMailList | undefined>(
      MAIL_LIST_STORE,
      "readonly",
      (s) => s.get(key) as IDBRequest<CachedMailList | undefined>,
    );
    return rec ?? null;
  } catch {
    return null;
  }
}

// ── Opened message / thread cache (offline read) ─────────────────────────────

export async function cacheMessage(id: string, data: unknown): Promise<void> {
  try {
    const rec: CachedMessage = { id, data, savedAt: Date.now() };
    await tx(MESSAGE_STORE, "readwrite", (s) => s.put(rec));
    await evictOldMessages();
  } catch {
    /* best effort */
  }
}

export async function readMessage<T = unknown>(id: string): Promise<T | null> {
  try {
    const rec = await tx<CachedMessage | undefined>(
      MESSAGE_STORE,
      "readonly",
      (s) => s.get(id) as IDBRequest<CachedMessage | undefined>,
    );
    return rec ? (rec.data as T) : null;
  } catch {
    return null;
  }
}

// Keep only the MESSAGE_CAP most-recently-saved messages.
async function evictOldMessages(): Promise<void> {
  try {
    const all = await getAll<CachedMessage>(MESSAGE_STORE);
    if (all.length <= MESSAGE_CAP) return;
    const doomed = all
      .sort((a, b) => a.savedAt - b.savedAt)
      .slice(0, all.length - MESSAGE_CAP);
    await Promise.all(doomed.map((m) => tx(MESSAGE_STORE, "readwrite", (s) => s.delete(m.id))));
  } catch {
    /* best effort */
  }
}

// ── App badge (unread count on the installed-app icon) ───────────────────────

// Feature-detected: the Badging API only exists in installed PWAs on a subset
// of browsers. A count of 0 (or a falsy value) clears the badge.
export function setUnreadBadge(count: number): void {
  try {
    const nav = navigator as Navigator & {
      setAppBadge?: (n?: number) => Promise<void>;
      clearAppBadge?: () => Promise<void>;
    };
    if (count > 0 && typeof nav.setAppBadge === "function") {
      void nav.setAppBadge(count).catch(() => {});
    } else if (typeof nav.clearAppBadge === "function") {
      void nav.clearAppBadge().catch(() => {});
    }
  } catch {
    /* Badging API absent or blocked — non-fatal */
  }
}

export function supportsAppBadge(): boolean {
  return typeof navigator !== "undefined" && "setAppBadge" in navigator;
}
